/**
 * Assessment 1 of the saya yield engine — **ölçek alanı**, the Russ & Small
 * parallelogram, per part.
 *
 * ── The assessment ──────────────────────────────────────────────────────────
 *
 * The classic footwear interlocking assessment: tile one part at its permitted
 * orientations and measure the repeating parallelogram cell that contains `k`
 * complete pieces. The cell area divided by `k` is what one piece consumes
 * INCLUDING the interlocking waste around it, which is the number a costing
 * sheet needs and a bare bounding box never gives.
 *
 *     scale_area_per_piece = |u × v| / k
 *     η_part               = A_net / scale_area_per_piece
 *
 * The orientation rule fixes the search space, and a denser layout that breaks
 * the rule is illegal rather than clever:
 *
 *   tek_yon    one-way material (napped nubuk, süet). Same-angle lattices only,
 *              k = 1.
 *   cift_yon   0°/180° allowed. A superset of tek_yon: the plain k = 1 lattice
 *              stays legal and is searched too, plus the 180° mate interlock
 *              (k = 2). The better of the two wins, so çift yön can never
 *              assess worse than tek yön.
 *   serbest    free. Angles 0…175 at `angleStepDeg` (default 5°), k = 1 and
 *              k = 2 both tried.
 *
 * Holes are ignored entirely. `A_net` is the OUTER ring's area (`partNetArea`
 * went outer-only — a punched hole does not reduce the leather a piece
 * consumes, and consumption is what yield is computed against), and the
 * lattice is built from the outer ring alone, so legacy hole rings still
 * present on stored parts move neither the cell nor the efficiency.
 *
 * ── How the lattice is found ────────────────────────────────────────────────
 *
 * Everything runs on the mm grid from `geometry.ts` (default 1 mm cell; read
 * the tolerance note there — this is an ESTIMATION engine). The lattice is
 * restricted to the "shelf shear" family
 *
 *     u = (dx, 0)        the row pitch, pieces repeat horizontally
 *     v = (sx, dy)       the row-to-row pitch, with a horizontal shear sx
 *
 * whose cell area is |u × v| = dx·dy regardless of the shear — so the shear is
 * free yield and the search is over two integers plus an offset. For a
 * candidate row pitch `dx` the module finds the SMALLEST `dy` for which some
 * shear `sx` keeps the whole infinite lattice collision-free, which is the
 * literal statement of the assessment. Collision-freedom is checked against
 * every lattice neighbour `m·u + n·v` at once: reducing the horizontal offset
 * modulo `dx` folds the infinite row into one residue class, so a single
 * lookup per `n` covers all `m`.
 *
 * The row pitch itself is searched over a bounded band starting at the tightest
 * feasible pitch (a slightly wider row sometimes buys a much shorter row-to-row
 * pitch), not over every possible value — see `CX_SPAN` / `CX_SAMPLES`. The
 * result is therefore a good lattice, not a proven optimum, and the module says
 * so: this is an assessment, and `algoVersion` is what makes a stored number
 * comparable to a later one.
 *
 * Fully deterministic: integer loops, fixed tie-breaks, no `Math.random`, no
 * time or environment input. Two runs on the same input produce byte-identical
 * output, which is what lets the Worker re-derive and reject a tampered
 * client-side result.
 */

import {
  DEFAULT_CELL_MM,
  type Part,
  type Point,
  type Raster,
  partNetArea,
  rasterDilate,
  rasterPad,
  rasterRuns,
  ringToRaster,
  rotate,
} from './geometry'

/* ── Public contract ─────────────────────────────────────────────────────── */

/** Direction rule for a part, as captured on the review screen. */
export type Orientation = 'tek_yon' | 'cift_yon' | 'serbest'

export interface ScaleAreaOptions {
  /** Direction rule — decides which angles and which `k` are legal. */
  orientation: Orientation
  /** Cutter clearance between neighbouring pieces, mm. */
  gapMm: number
  /** Assessment grid resolution, mm. Defaults to `DEFAULT_CELL_MM` (1 mm). */
  cellMm?: number
  /** Angle step for `serbest`, degrees. Defaults to 5°. Ignored otherwise. */
  angleStepDeg?: number
}

export interface ScaleAreaResult {
  /** |u × v| — the area of the repeating parallelogram, mm². */
  cellAreaMm2: number
  /** `cellAreaMm2 / k` — the ölçek alanı of ONE piece, mm². */
  perPieceMm2: number
  /** `A_net / perPieceMm2`, clamped to 1. */
  efficiency: number
  /** Complete pieces per cell: 1, or 2 when the 180° mate interlocks. */
  k: 1 | 2
  /** Angle the piece is laid at, degrees CCW. */
  angleDeg: number
  /** Row pitch vector, mm. */
  u: [number, number]
  /** Row-to-row pitch vector including the shear, mm. */
  v: [number, number]
  /**
   * Where the 180° mate sits inside the winning motif, mm — the translation
   * applied to `rotate(outer, angleDeg + 180)`. Non-null exactly when `k === 2`.
   * Purely descriptive (the visualisation draws the mate from it); adding it
   * moved no number, which is why `algoVersion` did not bump.
   */
  mateOffsetMm: [number, number] | null
  algoVersion: string
}

/**
 * Stamped onto every stored result. Bump it whenever a change here can move a
 * number, so `app.saya_calc` rows computed under different rules are never
 * compared as if they agreed.
 *
 * `yield-3`: the strip placer's rotation tie-break measures the skyline raise
 * from the shape's OCCUPIED rows rather than its raster frame height, which
 * used to charge a rotation for empty rows above its outline and could hand
 * the tie to the rotation that consumed MORE strip — strip numbers move; the
 * ölçek lattice itself is unchanged.
 */
export const YIELD_ALGO_VERSION = 'yield-3'

/** Default angle step for `serbest`: 0, 5, …, 175 — 36 candidate angles. */
export const DEFAULT_ANGLE_STEP_DEG = 5

/**
 * How far above the tightest feasible row pitch the search looks, in cells,
 * plus how many extra widely-spaced samples it takes up to the "rows no longer
 * interact" pitch. Widening a row occasionally shortens the row-to-row pitch by
 * more than it costs; sweeping every pitch would multiply the work by the part
 * width for a payoff that is nil on the shapes this factory cuts.
 */
const CX_SPAN = 8
const CX_SAMPLES = 4

/* ── Entry point ─────────────────────────────────────────────────────────── */

export function scaleAreaAssess(part: Part, opts: ScaleAreaOptions): ScaleAreaResult {
  const cellMm = opts.cellMm ?? DEFAULT_CELL_MM
  if (!(cellMm > 0)) throw new Error('scaleAreaAssess: cellMm must be > 0')
  if (!(opts.gapMm >= 0)) throw new Error('scaleAreaAssess: gapMm must be >= 0')
  const stepDeg = opts.angleStepDeg ?? DEFAULT_ANGLE_STEP_DEG
  if (!(stepDeg > 0)) throw new Error('scaleAreaAssess: angleStepDeg must be > 0')

  const netAreaMm2 = partNetArea(part)
  if (!(netAreaMm2 > 0)) {
    throw new Error('scaleAreaAssess: part outer ring must enclose a positive area')
  }

  const gapCells = Math.max(0, Math.ceil(opts.gapMm / cellMm))
  const angles = candidateAngles(opts.orientation, stepDeg)
  const ks = candidateKs(opts.orientation)

  let best: ScaleAreaResult | null = null

  for (let a = 0; a < angles.length; a++) {
    const angleDeg = angles[a] as number
    const outer = rotate(part.outer, angleDeg)
    const base = ringToRaster(outer, cellMm)

    for (let ki = 0; ki < ks.length; ki++) {
      const k = ks[ki] as 1 | 2
      const motifs: Motif[] =
        k === 1
          ? [{ raster: base, mateOffsetMm: null }]
          : mateMotifs(part.outer, angleDeg, cellMm, gapCells)
      for (let mi = 0; mi < motifs.length; mi++) {
        const motif = motifs[mi] as Motif
        const lattice = bestLattice(motif.raster, gapCells)
        if (lattice === null) continue
        const cellAreaMm2 = lattice.cx * lattice.cy * cellMm * cellMm
        const perPieceMm2 = cellAreaMm2 / k
        const candidate: ScaleAreaResult = {
          cellAreaMm2,
          perPieceMm2,
          // The packing term comes off a grid, so a coarse cell can in
          // principle shave a hair off the true pitch and push the ratio a
          // fraction above 1. Clamp: an efficiency over 100 % is not a finding,
          // it is grid noise, and the design validates η in (0, 1].
          efficiency: Math.min(1, netAreaMm2 / perPieceMm2),
          k,
          angleDeg,
          u: [lattice.cx * cellMm, 0],
          v: [lattice.sx * cellMm, lattice.cy * cellMm],
          mateOffsetMm: motif.mateOffsetMm,
          algoVersion: YIELD_ALGO_VERSION,
        }
        if (best === null || isBetter(candidate, best)) best = candidate
      }
    }
  }

  if (best === null) throw new Error('scaleAreaAssess: no lattice found for the part')
  return best
}

/**
 * Tie-break order, fixed so the answer never depends on iteration accidents:
 * smaller consumption first, then the smaller angle (0° is the one a cutter
 * expects to see), then the smaller `k` (a one-piece cell is the simpler
 * layout), then the tighter row pitch.
 */
function isBetter(a: ScaleAreaResult, b: ScaleAreaResult): boolean {
  if (a.perPieceMm2 !== b.perPieceMm2) return a.perPieceMm2 < b.perPieceMm2
  if (a.angleDeg !== b.angleDeg) return a.angleDeg < b.angleDeg
  if (a.k !== b.k) return a.k < b.k
  return a.u[0] < b.u[0]
}

function candidateAngles(orientation: Orientation, stepDeg: number): number[] {
  if (orientation !== 'serbest') return [0]
  const angles: number[] = []
  // Built from a counter rather than by accumulating the step, so 36 × 5 lands
  // exactly on 175 instead of drifting.
  for (let i = 0; i * stepDeg < 180; i++) angles.push(i * stepDeg)
  return angles
}

function candidateKs(orientation: Orientation): (1 | 2)[] {
  return orientation === 'tek_yon' ? [1] : [1, 2]
}

/* ── The 180° mate (k = 2) ───────────────────────────────────────────────── */

/**
 * A tiling candidate: the occupancy it is searched with, and — for a two-piece
 * motif — where the mate sits inside it, in millimetres, so the result can say
 * where the second piece goes instead of only how much the pair costs.
 */
interface Motif {
  raster: Raster
  mateOffsetMm: [number, number] | null
}

/**
 * Build the çift-yön motifs: the piece at `angleDeg` together with its 180°
 * mate, which is then tiled as ONE rigid shape carrying two pieces.
 *
 * The mate is placed at the smallest horizontal offset that keeps the gap, at a
 * few vertical offsets — head-to-head (`by = 0`, the offset that makes two L's
 * fold into a rectangle) and one staggered offset either way. That is a coarse
 * sample of the mate's placement, chosen because the full 2-D search costs far
 * more than the last percent it would find, and because everything the motif
 * search then does — every inter-motif collision, every lattice neighbour — is
 * still checked exactly.
 */
function mateMotifs(
  outer: readonly Point[],
  angleDeg: number,
  cellMm: number,
  gapCells: number,
): Motif[] {
  const a = ringToRaster(rotate(outer, angleDeg), cellMm)
  const b = ringToRaster(rotate(outer, angleDeg + 180), cellMm)
  if (a.width === 0 || a.height === 0 || b.width === 0 || b.height === 0) return []

  const dilA = rasterDilate(a, gapCells)
  const runsA = rasterRuns(dilA)
  const runsB = rasterRuns(b)
  const rowShift = b.originCellY - dilA.originCellY
  const colShift = b.originCellX - dilA.originCellX
  const reach = dilA.width + b.width + 2

  const motifs: Motif[] = []
  const seen = new Set<string>()
  const stagger = Math.round(a.height / 3)
  const byCandidates = stagger > 0 ? [0, stagger, -stagger] : [0]

  for (let c = 0; c < byCandidates.length; c++) {
    const by = byCandidates[c] as number
    const forbidden = forbiddenOffsets(runsA, dilA.height, runsB, b.height, rowShift + by)
    const bx = smallestFreeOffset(forbidden, colShift, reach)
    if (bx === null) continue
    const key = `${bx},${by}`
    if (seen.has(key)) continue
    seen.add(key)
    motifs.push({
      raster: unionAt(a, b, bx, by),
      // The offset is whole world cells, so the millimetre form is exact.
      mateOffsetMm: [bx * cellMm, by * cellMm],
    })
  }
  return motifs
}

/**
 * Smallest `|offset|` (positive preferred on a tie) whose generic column shift
 * is not forbidden. Always terminates: past `reach` the two rasters cannot
 * touch at all.
 */
function smallestFreeOffset(forbidden: number[], colShift: number, reach: number): number | null {
  for (let d = 0; d <= reach; d++) {
    if (!inIntervals(forbidden, colShift + d)) return d
    if (d > 0 && !inIntervals(forbidden, colShift - d)) return -d
  }
  return null
}

/** OR two rasters together, the second offset by `(bx, by)` world cells. */
function unionAt(a: Raster, b: Raster, bx: number, by: number): Raster {
  const bOriginX = b.originCellX + bx
  const bOriginY = b.originCellY + by
  const originCellX = Math.min(a.originCellX, bOriginX)
  const originCellY = Math.min(a.originCellY, bOriginY)
  const width = Math.max(a.originCellX + a.width, bOriginX + b.width) - originCellX
  const height = Math.max(a.originCellY + a.height, bOriginY + b.height) - originCellY
  const data = new Uint8Array(width * height)
  blit(data, width, a, a.originCellX - originCellX, a.originCellY - originCellY)
  blit(data, width, b, bOriginX - originCellX, bOriginY - originCellY)
  return { cellMm: a.cellMm, originCellX, originCellY, width, height, data }
}

function blit(data: Uint8Array, width: number, src: Raster, dx: number, dy: number): void {
  for (let j = 0; j < src.height; j++) {
    const srcBase = j * src.width
    const dstBase = (j + dy) * width + dx
    for (let i = 0; i < src.width; i++) {
      if (src.data[srcBase + i] !== 0) data[dstBase + i] = 1
    }
  }
}

/* ── Lattice search ──────────────────────────────────────────────────────── */

interface Lattice {
  /** Row pitch, cells. */
  cx: number
  /** Row-to-row pitch, cells. */
  cy: number
  /** Horizontal shear of the row-to-row pitch, cells, in `[0, cx)`. */
  sx: number
}

function bestLattice(motif: Raster, gapCells: number): Lattice | null {
  if (motif.width === 0 || motif.height === 0) return null
  const dilated = rasterDilate(motif, gapCells)
  const padded = rasterPad(motif, gapCells)
  const h = dilated.height
  const w = dilated.width
  const runsD = rasterRuns(dilated)
  const runsP = rasterRuns(padded)

  // Forbidden horizontal offsets for every non-negative row offset. Row offsets
  // at or beyond `h` cannot overlap at all, so the table stops there; the
  // negative side is the mirror image of the positive one and never needs
  // computing (see the symmetry note on `rasterDilate`).
  const forbidden: number[][] = new Array(h)
  for (let dy = 0; dy < h; dy++) {
    forbidden[dy] = forbiddenOffsets(runsD, h, runsP, h, dy)
  }
  const atRest = forbidden[0] as number[]

  const cxMin = tightestRowPitch(atRest, w)
  if (cxMin === null) return null

  let best: Lattice | null = null
  const pitches = rowPitchCandidates(cxMin, w)
  for (let p = 0; p < pitches.length; p++) {
    const cx = pitches[p] as number
    if (!rowPitchIsFree(atRest, cx, w)) continue
    const stack = shortestRowStack(forbidden, h, cx)
    if (stack === null) continue
    if (best === null || cx * stack.cy < best.cx * best.cy) {
      best = { cx, cy: stack.cy, sx: stack.sx }
    }
  }
  return best
}

/**
 * A row pitch is legal only when EVERY multiple of it clears — pieces repeat
 * along the whole row, not just to the immediate neighbour. Offsets of `w` or
 * more can never overlap, which bounds both loops.
 */
function rowPitchIsFree(atRest: number[], cx: number, w: number): boolean {
  for (let m = cx; m < w; m += cx) {
    if (inIntervals(atRest, m)) return false
  }
  return true
}

function tightestRowPitch(atRest: number[], w: number): number | null {
  for (let cx = 1; cx <= w; cx++) {
    if (rowPitchIsFree(atRest, cx, w)) return cx
  }
  return null
}

function rowPitchCandidates(cxMin: number, w: number): number[] {
  const out: number[] = []
  const dense = Math.min(w, cxMin + CX_SPAN)
  for (let cx = cxMin; cx <= dense; cx++) out.push(cx)
  if (w > dense) {
    for (let s = 1; s <= CX_SAMPLES; s++) {
      const cx = dense + Math.round((s * (w - dense)) / CX_SAMPLES)
      if (cx <= w && out[out.length - 1] !== cx) out.push(cx)
    }
  }
  return out
}

/**
 * Shortest row-to-row pitch for a given row pitch, together with the shear that
 * achieves it.
 *
 * For a shear `sx` the lattice puts row `n` at horizontal offset `n·sx` — and
 * because the row itself repeats every `cx`, only `n·sx mod cx` matters, which
 * is what makes one array lookup per `n` cover every neighbour in that row.
 * Scanning `cy` upwards and `sx` upwards inside it returns the shortest pitch,
 * and on a tie the smallest shear.
 */
function shortestRowStack(
  forbidden: number[][],
  h: number,
  cx: number,
): { cy: number; sx: number } | null {
  const cache: (Uint8Array | null)[] = new Array(h).fill(null)
  for (let cy = 1; cy <= h; cy++) {
    for (let sx = 0; sx < cx; sx++) {
      let ok = true
      for (let n = 1; n * cy < h; n++) {
        const dy = n * cy
        let allowed = cache[dy] ?? null
        if (allowed === null) {
          allowed = allowedMask(forbidden[dy] as number[], cx)
          cache[dy] = allowed
        }
        if (allowed[(n * sx) % cx] === 0) {
          ok = false
          break
        }
      }
      if (ok) return { cy, sx }
    }
  }
  return null
}

/**
 * Fold the forbidden offset intervals of one row offset into a `cx`-wide
 * residue mask: `mask[d] === 1` means a shear of `d` clears every horizontal
 * repeat of the row at that row offset.
 */
function allowedMask(intervals: number[], cx: number): Uint8Array {
  const mask = new Uint8Array(cx).fill(1)
  for (let t = 0; t < intervals.length; t += 2) {
    const lo = intervals[t] as number
    const hi = intervals[t + 1] as number
    const span = hi - lo + 1
    if (span >= cx) {
      mask.fill(0)
      return mask
    }
    let p = ((lo % cx) + cx) % cx
    for (let q = 0; q < span; q++) {
      mask[p] = 0
      p++
      if (p === cx) p = 0
    }
  }
  return mask
}

/* ── Collision as intervals ──────────────────────────────────────────────── */

/**
 * All horizontal offsets at which raster A collides with raster B shifted by
 * `(dx, dy)` cells, returned as a sorted, merged list of closed intervals
 * `[lo, hi, lo, hi, …]`.
 *
 * The whole search rests on one observation: a run `[as, ae)` of A and a run
 * `[bs, be)` of B overlap exactly when `as − be < dx < ae − bs`. So each pair
 * of runs contributes one interval, and the answer for a row offset is the
 * union of a handful of intervals rather than a cell-by-cell correlation.
 */
function forbiddenOffsets(
  runsA: number[][],
  heightA: number,
  runsB: number[][],
  heightB: number,
  dy: number,
): number[] {
  const raw: number[] = []
  const jStart = Math.max(0, dy)
  const jEnd = Math.min(heightA, heightB + dy)
  for (let j = jStart; j < jEnd; j++) {
    const rowA = runsA[j] as number[]
    if (rowA.length === 0) continue
    const rowB = runsB[j - dy] as number[]
    if (rowB.length === 0) continue
    for (let x = 0; x < rowA.length; x += 2) {
      const as = rowA[x] as number
      const ae = rowA[x + 1] as number
      for (let y = 0; y < rowB.length; y += 2) {
        const bs = rowB[y] as number
        const be = rowB[y + 1] as number
        raw.push(as - be + 1, ae - bs - 1)
      }
    }
  }
  return mergeIntervals(raw)
}

function mergeIntervals(raw: number[]): number[] {
  const count = raw.length / 2
  if (count === 0) return []
  const order: number[] = new Array(count)
  for (let i = 0; i < count; i++) order[i] = i
  order.sort((p, q) => (raw[2 * p] as number) - (raw[2 * q] as number))
  const out: number[] = []
  for (let i = 0; i < count; i++) {
    const at = order[i] as number
    const lo = raw[2 * at] as number
    const hi = raw[2 * at + 1] as number
    if (hi < lo) continue
    const last = out.length - 1
    if (out.length > 0 && lo <= (out[last] as number) + 1) {
      if (hi > (out[last] as number)) out[last] = hi
    } else {
      out.push(lo, hi)
    }
  }
  return out
}

function inIntervals(intervals: number[], value: number): boolean {
  for (let t = 0; t < intervals.length; t += 2) {
    if (value < (intervals[t] as number)) return false
    if (value <= (intervals[t + 1] as number)) return true
  }
  return false
}
